import React, { useState, useEffect } from "react";
import { MessageSquare, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import useAuth from "../../services/useAuth"; 

const FinalCTA = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { api } = useAuth(); 

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  // Prevent background scrolling when modal is active
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isModalOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("All fields are required.");
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading("Connecting to lead guides...");

    try {
      // Backend must have a POST route for /api/newsletter/inquiry
      const response = await api.post("/api/newsletter/inquiry", formData);

      if (response.data && response.data.status === "success") {
        toast.success("Inquiry sent successfully.");
        setIsModalOpen(false);
        setFormData({ name: "", email: "", message: "" });
      } else {
        toast.error(response.data?.message || "Could not send inquiry.");
      }
    } catch (err) {
      toast.error("Server error. Please try again later.");
      console.error("Inquiry Error:", err);
    } finally {
      setIsSubmitting(false);
      toast.dismiss(loadingToast);
    }
  };

  return (
    // 'w-full' and 'm-0' are critical here to stop the side spacing
    <section className="w-full bg-[#575C3E] pt-24 pb-20 px-6 relative overflow-hidden m-0">
      
      {/* Inner container controls content width, while section bg bleeds full-width */}
      <div className="max-w-4xl mx-auto text-center relative z-10">
        <h2 className="text-[#C99D89] uppercase tracking-[0.4em] text-[10px] font-bold mb-6 popp">
          Limited Expeditions
        </h2>
        <h3 className="text-4xl md:text-6xl font-serif text-[#FCFCFC] italic mb-10 leading-tight">
          A few peaks, mastered.
        </h3>
        
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
          <Link to="/packages" className="bg-[#C99D89] hover:bg-[#b88c78] text-white px-10 py-4 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all flex items-center gap-3 popp">
            Explore the Collection
            <ArrowRight className="size-4" />
          </Link>
          
          <button 
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-widest text-[#FCFCFC] hover:text-[#C99D89] transition-colors popp"
          >
            <MessageSquare className="size-4" />
            Talk to a Lead Guide
          </button>
        </div>
      </div>

      {/* MODAL SYSTEM */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm">
          {/* Clickable Backdrop (closes modal) */}
          <div className="absolute inset-0" onClick={() => !isSubmitting && setIsModalOpen(false)} />

          <div className="bg-[#FCFCFC] w-full max-w-lg rounded-[32px] p-8 md:p-12 relative z-10 shadow-2xl">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 text-[#575C3E]/40 hover:text-[#575C3E] transition-colors"
            >
              <X size={24} />
            </button>

            <h4 className="text-[#575C3E] text-3xl font-serif italic mb-2">Direct Inquiry</h4>
            <p className="text-[#575C3E]/60 text-[10px] uppercase tracking-widest mb-8 popp">
              Message our veteran guides directly.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <input 
                type="text" 
                placeholder="Full Name" 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full bg-transparent border-b border-[#575C3E]/20 py-3 outline-none text-[#575C3E] focus:border-[#C99D89] transition-colors popp"
              />
              <input 
                type="email" 
                placeholder="Email Address" 
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full bg-transparent border-b border-[#575C3E]/20 py-3 outline-none text-[#575C3E] focus:border-[#C99D89] transition-colors popp"
              />
              <textarea 
                placeholder="How can we help you plan your journey?" 
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows={4}
                className="w-full bg-transparent border-b border-[#575C3E]/20 py-3 outline-none text-[#575C3E] focus:border-[#C99D89] transition-colors resize-none popp"
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#575C3E] text-white py-4 rounded-full text-[11px] font-bold uppercase tracking-widest hover:bg-[#C99D89] transition-all popp ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default FinalCTA;